/**
 * OracleAdminDashboard.jsx - The Oracle Admin Control Center
 * 
 * A comprehensive admin dashboard for managing Astral Belonging's:
 * - Token usage and costs (Token Watcher)
 * - AI Kill Switch
 * - User support & management (delete, notes)
 * - AI quality monitoring
 * - Email Analytics & Testing
 * 
 * Only accessible to users with is_admin: true
 * 
 * Components extracted to /components/admin/:
 * - OverviewTab, UsersTab, SystemHealthTab, AlertsTab, EmailAnalyticsSection, AIQualityMonitor
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Shield, RefreshCw, XCircle, BarChart3, ArrowLeft,
    Loader2, Mail, Server, Users, Bell
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getAuthHeaders } from '../utils/api';

// Import all admin components from the admin module
import { API_URL, AlertsTab, OverviewTab, UsersTab, SystemHealthTab, EmailAnalyticsSection, NeonIcon, OrbitsTab } from './admin';
import { Orbit } from 'lucide-react';

/**
 * Main Admin Dashboard Component
 */
const OracleAdminDashboard = ({ onClose }) => {
    const { language } = useLanguage();
    const isSpanish = language?.startsWith('es');
    
    // Tab state
    const [activeTab, setActiveTab] = useState('overview');
    
    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState([]);
    const [aiResponses, setAiResponses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [killSwitchLoading, setKillSwitchLoading] = useState(false);
    
    // User search state
    const [searchEmail, setSearchEmail] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    
    // Users list state
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersPage, setUsersPage] = useState(0);
    
    // System health state
    const [systemHealth, setSystemHealth] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [dnsStatus, setDnsStatus] = useState(null);
    
    // Fetch dashboard data
    const fetchDashboardData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const authHeaders = await getAuthHeaders();
            
            // Add internal secret header for admin endpoints
            const headers = {
                ...authHeaders,
                'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
            };
            
            // Fetch stats
            const statsRes = await fetch(`${API_URL}/api/admin/stats?days=7`, {
                credentials: 'include',
                headers
            });
            
            if (!statsRes.ok) {
                if (statsRes.status === 403) {
                    throw new Error('Admin access required - your account may not have admin privileges');
                }
                if (statsRes.status === 401) {
                    throw new Error('Authentication required - please log in again');
                }
                const errorText = await statsRes.text().catch(() => '');
                throw new Error(`Failed to fetch stats (${statsRes.status}): ${errorText || 'Server error'}`);
            }
            
            const statsData = await statsRes.json();
            setStats(statsData);
            
            // Fetch recent logs
            const logsRes = await fetch(`${API_URL}/api/admin/logs?limit=20`, {
                credentials: 'include',
                headers
            });
            
            if (logsRes.ok) {
                const logsData = await logsRes.json();
                setLogs(logsData.logs || []);
            }
            
            // Fetch AI quality logs
            const aiRes = await fetch(`${API_URL}/api/admin/ai-quality?limit=10`, {
                credentials: 'include',
                headers
            });
            
            if (aiRes.ok) {
                const aiData = await aiRes.json();
                setAiResponses(aiData.responses || []);
            }
            
        } catch (e) {
            console.error('Dashboard error:', e);
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    }, []);
    
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);
    
    // Toggle Kill Switch
    const handleToggleKillSwitch = async () => {
        if (killSwitchLoading) return;
        
        const newState = !stats?.kill_switch_active;
        const confirmMsg = newState
            ? (isSpanish ? '¿DESACTIVAR todas las funciones de IA?' : 'DISABLE all AI features?')
            : (isSpanish ? '¿Reactivar las funciones de IA?' : 'Re-enable AI features?');
        
        if (!window.confirm(confirmMsg)) return;
        
        const reason = newState ? window.prompt(isSpanish ? 'Razón para desactivar:' : 'Reason for disabling:') : 'Re-enabled';
        
        setKillSwitchLoading(true);
        
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/admin/kill-switch`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify({ enabled: newState, reason })
            });
            
            if (res.ok) {
                setStats(prev => ({ ...prev, kill_switch_active: newState }));
            }
        } catch (e) {
            console.error('Kill switch error:', e);
        } finally {
            setKillSwitchLoading(false);
        }
    };
    
    // Search users
    const handleSearchUsers = async () => {
        if (!searchEmail.trim()) return;
        
        setSearchLoading(true);
        
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/admin/users/search?email=${encodeURIComponent(searchEmail)}`, {
                credentials: 'include',
                headers
            });
            
            if (res.ok) {
                const data = await res.json();
                setSearchResults(data.users || []);
            }
        } catch (e) {
            console.error('Search error:', e);
        } finally {
            setSearchLoading(false);
        }
    };
    
    // Users stats state
    const [usersStats, setUsersStats] = useState(null);
    const [usersTotal, setUsersTotal] = useState(0);
    
    // Fetch users list
    const fetchUsers = useCallback(async (page = 0) => {
        setUsersLoading(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/admin/users?limit=20&skip=${page * 20}`, {
                credentials: 'include',
                headers
            });
            if (res.ok) {
                const data = await res.json();
                setUsers(data.users || []);
                setUsersStats(data.stats || null);
                setUsersTotal(data.total || 0);
                setUsersPage(page);
            }
        } catch (e) {
            console.error('Users fetch error:', e);
        } finally {
            setUsersLoading(false);
        }
    }, []);
    
    // Fetch system health
    const fetchSystemHealth = useCallback(async () => {
        setHealthLoading(true);
        try {
            const headers = await getAuthHeaders();
            
            // Fetch our new comprehensive health endpoint
            const healthRes = await fetch(`${API_URL}/api/admin/system/health`, {
                credentials: 'include',
                headers
            });
            
            // Fetch DNS status
            const dnsRes = await fetch(`${API_URL}/api/admin/dns-check`, {
                credentials: 'include',
                headers
            });
            
            const health = healthRes.ok ? await healthRes.json() : null;
            const dns = dnsRes.ok ? await dnsRes.json() : null;
            
            setSystemHealth(health);
            setDnsStatus(dns);
            
        } catch (e) {
            console.error('Health fetch error:', e);
        } finally {
            setHealthLoading(false);
        }
    }, []);
    
    // Load tab data when tab changes
    useEffect(() => {
        if (activeTab === 'users' && users.length === 0) {
            fetchUsers(0);
        } else if (activeTab === 'system' && !systemHealth) {
            fetchSystemHealth();
        }
    }, [activeTab, users.length, systemHealth, fetchUsers, fetchSystemHealth]);
    
    // Rate AI response
    const handleRateResponse = async (memoryId, rating) => {
        try {
            const headers = await getAuthHeaders();
            await fetch(`${API_URL}/api/admin/ai-quality/${memoryId}/rate?rating=${rating}`, {
                method: 'POST',
                credentials: 'include',
                headers
            });
            
            // Update local state
            setAiResponses(prev => prev.map(r => 
                (r.insight_id === memoryId || r.timestamp === memoryId)
                    ? { ...r, admin_rating: rating }
                    : r
            ));
        } catch (e) {
            console.error('Rating error:', e);
        }
    };
    
    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                    <p className="text-primary/80">Loading Oracle Control Center...</p>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50">
                <div className="text-center p-8 bg-red-500/10 rounded-2xl border border-red-500/30 max-w-md">
                    <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-red-400 mb-2">
                        {error.includes('401') ? 'Session Expired' : 
                         error.includes('403') ? 'Access Denied' : 
                         'Connection Error'}
                    </h2>
                    <p className="text-white/60 mb-4">{error}</p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={fetchDashboardData}
                            className="px-4 py-2 bg-primary/20 rounded-lg hover:bg-primary/30 text-primary"
                        >
                            Retry
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 text-white"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-black/80 backdrop-blur-lg border-b border-primary/20 p-4 z-10">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/10 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-white/70" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-primary flex items-center gap-2">
                                <Shield className="w-5 h-5" />
                                Oracle Control Center
                            </h1>
                            <p className="text-xs text-white/50">Astral Belonging Admin Dashboard</p>
                        </div>
                    </div>
                    <button
                        onClick={fetchDashboardData}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className="w-5 h-5 text-white/70" />
                    </button>
                </div>
                
                {/* Tab Navigation */}
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {[
                        { id: 'overview', label: isSpanish ? 'General' : 'Overview', icon: BarChart3, neonColor: 'yellow' },
                        { id: 'alerts', label: isSpanish ? 'Alertas' : 'Alerts', icon: Bell, neonColor: 'red' },
                        { id: 'users', label: isSpanish ? 'Usuarios' : 'Users', icon: Users, neonColor: 'purple' },
                        { id: 'orbits', label: isSpanish ? 'Órbitas' : 'Orbits', icon: Orbit, neonColor: 'pink' },
                        { id: 'system', label: isSpanish ? 'Sistema' : 'System Health', icon: Server, neonColor: 'green' },
                        { id: 'email', label: isSpanish ? 'Correos' : 'Emails', icon: Mail, neonColor: 'cyan' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`
                                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                                ${activeTab === tab.id 
                                    ? 'bg-primary/20 text-primary border border-primary/30' 
                                    : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                                }
                            `}
                            data-testid={`admin-tab-${tab.id}`}
                        >
                            <NeonIcon icon={tab.icon} neonColor={activeTab === tab.id ? tab.neonColor : 'cyan'} size="w-4 h-4" animate={false} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>
            
            {/* Tab Content */}
            <div className="max-w-7xl mx-auto p-4">
                <AnimatePresence mode="wait">
                    {activeTab === 'overview' && (
                        <motion.div
                            key="overview"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <OverviewTab 
                                stats={stats}
                                logs={logs}
                                aiResponses={aiResponses}
                                searchEmail={searchEmail}
                                setSearchEmail={setSearchEmail}
                                searchResults={searchResults}
                                searchLoading={searchLoading}
                                handleSearchUsers={handleSearchUsers}
                                handleToggleKillSwitch={handleToggleKillSwitch}
                                killSwitchLoading={killSwitchLoading}
                                handleRateResponse={handleRateResponse}
                                isSpanish={isSpanish}
                            />
                        </motion.div>
                    )}
                    
                    {activeTab === 'users' && (
                        <motion.div
                            key="users"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <UsersTab 
                                users={users}
                                usersStats={usersStats}
                                usersTotal={usersTotal}
                                usersPage={usersPage}
                                usersLoading={usersLoading}
                                fetchUsers={fetchUsers}
                                isSpanish={isSpanish}
                            />
                        </motion.div>
                    )}
                    
                    {activeTab === 'system' && (
                        <motion.div
                            key="system"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <SystemHealthTab 
                                systemHealth={systemHealth}
                                dnsStatus={dnsStatus}
                                healthLoading={healthLoading}
                                fetchSystemHealth={fetchSystemHealth}
                                isSpanish={isSpanish}
                            />
                        </motion.div>
                    )}
                    
                    {activeTab === 'orbits' && (
                        <motion.div
                            key="orbits"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <OrbitsTab />
                        </motion.div>
                    )}
                    
                    {activeTab === 'email' && (
                        <motion.div
                            key="email"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <EmailAnalyticsSection isSpanish={isSpanish} expanded={true} />
                        </motion.div>
                    )}
                    
                    {activeTab === 'alerts' && (
                        <motion.div
                            key="alerts"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <AlertsTab isSpanish={isSpanish} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default OracleAdminDashboard;
